"use client";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { Config } from "@thriwe-2-0/thriwe-ui-utils";
import ToastUtils from "../toast/toast-utils";
import { ClientAssets } from "./client-assets-config/marketplace/client-assets";
import { IconKey } from "./client-assets-config/marketplace/icon-registry";
import { ClientStringConstants } from "./client-assets-config/marketplace/string-constants";
import { AppIcon } from "./icon-type";
import {
  createNewInstanceForAssets,
  createNewInstanceForStringConstants,
  getClientConfig,
} from "./project-config";

interface ClientConfigProvider {
  config?: Config;
  clientAssets?: ClientAssets;
  clientIcons: Partial<Record<IconKey, AppIcon>>;

  getImagePath: (image: string) => string;
  getImageWithBasePath: (image: string) => string;
  StringConstantsInstance?: ClientStringConstants;
  categoryLogos: Record<
    string,
    { defaultLogo: string; activeLogo: string }
  > | null;
  amenitiesIcons: Record<string, string> | null;
}

var ConfigProviderContext = createContext<ClientConfigProvider>({
  config: undefined,
  clientIcons: {},
  getImagePath: () => {
    return "";
  },
  getImageWithBasePath: () => {
    return "";
  },
  StringConstantsInstance: undefined,
  categoryLogos: null,
  amenitiesIcons: null,
});

export function ConfigProviderWrapper({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<Config>();
  const [isCssLoaded, setIsCssLoaded] = useState(false);
  const [isAssetsLoaded, setIsAssetsLoaded] = useState(false);
  const [clientAssets, setClientAssets] = useState<ClientAssets>();
  const [clientIcons, setClientIcons] = useState<
    Partial<Record<IconKey, AppIcon>>
  >({});

  const [categoryLogos, setCategoryLogos] = useState<Record<
    string,
    { defaultLogo: string; activeLogo: string }
  > | null>(null);
  const [amenitiesIcons, setAmenitiesIcons] = useState<Record<
    string,
    string
  > | null>(null);

  useEffect(() => {
    const projectCode = process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET;
    if (projectCode === "SCB_V1") {
      const loadCategoryLogos = async () => {
        try {
          const logosModule = await import(
            `@/app/config/client-assets-config/${projectCode}/category-logos.ts`
          );
          setCategoryLogos(logosModule.default);
        } catch (error) {
          console.warn(`No category-logos.ts found for ${projectCode}`);
          setCategoryLogos(null);
        }
      };

      loadCategoryLogos();
    }
  }, []);
  useEffect(() => {
    const projectCode = process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET;
    if (projectCode === "SCB_V1") {
      const loadAmenitiesIcons = async () => {
        try {
          const logosModule = await import(
            `@/app/config/client-assets-config/${projectCode}/ameneties-icons`
          );
          setAmenitiesIcons(logosModule.default);
        } catch (error) {
          console.warn(`No ameneties-icons.ts found for ${projectCode}`);
          setAmenitiesIcons(null);
        }
      };
      loadAmenitiesIcons();
    }
  }, []);

  const [constantInstance, setStringConstantInstance] =
    useState<ClientStringConstants>();
  useEffect(() => {
    setUpProjectWiseCSS();

    getSetConfig();
  }, []);

  useEffect(() => {
    const version = config?.toastVersion ?? config?.designVersion ?? "v1";
    ToastUtils.setToastVersion(version);
  }, [config?.designVersion]);

  const getSetConfig = async () => {
    const config = await getClientConfig();
    const StringConstant = await createNewInstanceForStringConstants();
    const AssetsConstant = await createNewInstanceForAssets();
    setConfig(config);
    setStringConstantInstance(new StringConstant());
    setClientAssets(new AssetsConstant());
  };

  useEffect(() => {
    const loadRegistry = async () => {
      const assetPath = process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET;
      const projectName = process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET;

      try {
        const importedModule = await import(
          `@/app/config/client-assets-config/${assetPath}/icon-registry.ts`
        );
        setClientIcons(importedModule.default);
      } catch (error) {
        console.warn(
          `Falling back to default icon registry for ${projectName}`
        );
        const fallbackModule = await import(
          "@/app/config/client-assets-config/marketplace/icon-registry"
        );
        setClientIcons(fallbackModule.default);
      }
    };

    loadRegistry();
  }, []);

  function setUpProjectWiseCSS() {
    const ProjectName = process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET;
    console.log("Setting up project name", ProjectName);

    import(`@/app/config/client-assets-config/${ProjectName}/all-css.scss`)
      .then((module) => {
        console.log("File 1 loaded");
        /* for (const [key, value] of Object.entries(workflow)) {
        document.documentElement.style.setProperty(`--${key}`, value);
      } */
        setIsCssLoaded(true);
      })
      .catch((error) => {
        console.error("Error loading file:", error);
        setIsCssLoaded(false);
      });
  }

  // if (!isCssLoaded) {
  //   return <div></div>;
  // }

  function getImagePath(image: string) {
    const path = `/client-assets/${process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET}/${image}`;
    return path;
  }

  function getImageWithBasePath(json: string) {
    // const path = `/client-assets/${process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET}/${image}`;
    return "";
  }

  return (
    <ConfigProviderContext.Provider
      value={{
        config: config,
        clientAssets: clientAssets,
        clientIcons: clientIcons,
        getImagePath: getImagePath,
        getImageWithBasePath: getImageWithBasePath,
        StringConstantsInstance: constantInstance,
        categoryLogos: categoryLogos,
        amenitiesIcons: amenitiesIcons,
      }}
    >
      {children}
    </ConfigProviderContext.Provider>
  );
}

export function useClientConfigProvider() {
  return useContext(ConfigProviderContext);
}
